/**
 * Eksport modelu 3D arkusza tworzywa do formatu Wavefront OBJ + ZIP.
 *
 * Struktura ZIP:
 *   sheet.obj          – geometria (wierzchołki, UV, normalne, ściany)
 *   sheet.mtl          – materiały (top / bottom / sides)
 *   top_texture.png    – tekstura górnej powierzchni
 *   bottom_texture.png – tekstura dolnej powierzchni (z nałożonym kolorem materiału)
 *
 * Jednostki: mm.  Arkusz leży w płaszczyźnie XY:
 *   z =  0     → górna powierzchnia
 *   z = -T     → dolna powierzchnia (grubość T mm)
 */

import JSZip from 'jszip';
import { AppState, TriangulationResult } from '../types';

// ── Helpers ──────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16 & 0xff) / 255, (n >> 8 & 0xff) / 255, (n & 0xff) / 255];
}

/** Buduje canvas z teksturą + nałożonym kolorem materiału (opacity = 1-transparency/100). */
async function buildOverlayTexture(
  img: HTMLImageElement,
  materialColor: string,
  transparency: number,
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width  = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const opacity = 1 - transparency / 100;
  if (opacity > 0) {
    ctx.globalAlpha = opacity;
    ctx.fillStyle   = materialColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;
  }
  return canvas.toDataURL('image/png');
}

/** Buduje canvas z jednolitym kolorem materiału (fallback gdy brak warstw). */
function buildSolidColorTexture(color: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 64, 64);
  return canvas.toDataURL('image/png');
}

/** Krawędzie graniczne (pojawiają się dokładnie raz wśród wszystkich trójkątów). */
function findBoundaryEdges(
  triangles: [number, number, number][],
): [number, number][] {
  const count = new Map<string, number>();
  const first = new Map<string, [number, number]>();
  for (const [a, b, c] of triangles) {
    for (const [u, v] of [[a, b], [b, c], [c, a]] as [number, number][]) {
      const key = u < v ? `${u}_${v}` : `${v}_${u}`;
      count.set(key, (count.get(key) ?? 0) + 1);
      if (!first.has(key)) first.set(key, [u, v]);
    }
  }
  const result: [number, number][] = [];
  for (const [key, cnt] of Array.from(count)) {
    if (cnt === 1) result.push(first.get(key)!);
  }
  return result;
}

// ── Główna funkcja eksportu ───────────────────────────────────

export async function exportSheetAsObjZip(
  tri:            TriangulationResult,
  appState:       AppState,
  textureDataUrl: string | null,
  zPositionsMm:   number[] | null = null,
  xPositionsMm:   number[] | null = null,
  yPositionsMm:   number[] | null = null,
): Promise<void> {
  const {
    materialWidth:  W,
    materialHeight: H,
    thicknessMm:    T,
    materialColor,
    transparency,
    materialAboveImage,
  } = appState;

  const { vertices, triCutLabels } = tri;
  // Gdy showFullMaterial=false, eksportujemy tylko zachowane trójkąty
  const triangles = (appState.showFullMaterial || !triCutLabels?.length)
    ? tri.triangles
    : tri.triangles.filter((_, i) => triCutLabels[i] === 0);
  const n  = vertices.length;
  const hw = W / 2;
  const hh = H / 2;

  // ── 1. Tekstury ───────────────────────────────────────────

  let topTexDataUrl:    string;
  let bottomTexDataUrl: string;
  let topTexFile:    string;
  let bottomTexFile: string;

  if (textureDataUrl) {
    const img        = await loadImage(textureDataUrl);
    const overlayUrl = await buildOverlayTexture(img, materialColor, transparency);

    if (materialAboveImage) {
      // Nadruk od dołu: dół = czysta tekstura, góra = z nałożonym materiałem
      topTexDataUrl    = overlayUrl;
      bottomTexDataUrl = textureDataUrl;
    } else {
      // Nadruk od góry (domyślnie): góra = czysta, dół = z nałożonym materiałem
      topTexDataUrl    = textureDataUrl;
      bottomTexDataUrl = overlayUrl;
    }
    topTexFile    = 'top_texture.png';
    bottomTexFile = 'bottom_texture.png';
  } else {
    const solid      = buildSolidColorTexture(materialColor);
    topTexDataUrl    = solid;
    bottomTexDataUrl = solid;
    topTexFile    = 'material_color.png';
    bottomTexFile = 'material_color.png';
  }

  // ── 2. Geometria pomocnicza ───────────────────────────────

  const boundaryEdges = findBoundaryEdges(triangles);

  // Helpers: aktualne pozycje węzła i po symulacji (mm).
  const xOf = (i: number) => (xPositionsMm ? xPositionsMm[i] ?? vertices[i].x : vertices[i].x);
  const yOf = (i: number) => (yPositionsMm ? yPositionsMm[i] ?? vertices[i].y : vertices[i].y);
  const zTop = (i: number) => (zPositionsMm ? zPositionsMm[i] ?? 0 : 0);

  // --- Oblicz normalne wierzchołków dla poprawnego offsetu grubości ---
  const vertexNormals: Array<{ x: number, y: number, z: number }> =
    new Array(n).fill(null).map(() => ({ x: 0, y: 0, z: 1 }));

  const accumNormals = new Float64Array(n * 3);
  for (const [a, b, c] of triangles) {
    const ax = xOf(a), ay = yOf(a), az = zTop(a);
    const bx = xOf(b), by = yOf(b), bz = zTop(b);
    const cx = xOf(c), cy = yOf(c), cz = zTop(c);
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    // Iloczyn wektorowy (normalna ściany)
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    for (const idx of [a, b, c]) {
      accumNormals[idx * 3]     += nx;
      accumNormals[idx * 3 + 1] += ny;
      accumNormals[idx * 3 + 2] += nz;
    }
  }
  for (let i = 0; i < n; i++) {
    const nx = accumNormals[i * 3];
    const ny = accumNormals[i * 3 + 1];
    const nz = accumNormals[i * 3 + 2];
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      vertexNormals[i] = { x: nx / len, y: ny / len, z: nz / len };
    }
  }

  // ── 3. OBJ ───────────────────────────────────────────────

  const obj: string[] = [];
  obj.push('# Picture Dome Designer – Sheet Model');
  obj.push(`# Width: ${W} mm  Height: ${H} mm  Thickness: ${T} mm`);
  obj.push('mtllib sheet.mtl');
  obj.push('');

  // Wierzchołki górne (1..n)
  obj.push('# Vertices – top surface');
  for (let i = 0; i < n; i++) {
    obj.push(`v ${xOf(i).toFixed(4)} ${yOf(i).toFixed(4)} ${zTop(i).toFixed(4)}`);
  }

  // Wierzchołki dolne (n+1..2n) – przesunięte wzdłuż normalnych
  obj.push('# Vertices – bottom surface (offset along normals)');
  const bottomVerts: Array<{ x: number; y: number; z: number }> = new Array(n);
  for (let i = 0; i < n; i++) {
    const vNorm = vertexNormals[i];
    const bx = xOf(i) - vNorm.x * T;
    const by = yOf(i) - vNorm.y * T;
    const bz = zTop(i) - vNorm.z * T;
    bottomVerts[i] = { x: bx, y: by, z: bz };
    obj.push(`v ${bx.toFixed(4)} ${by.toFixed(4)} ${bz.toFixed(4)}`);
  }

  // Wierzchołki boczne (2n+1 .. 2n+4*numEdges), 4 na krawędź
  obj.push('# Vertices – side walls');
  for (const [u, v] of boundaryEdges) {
    const bu = bottomVerts[u];
    const bv = bottomVerts[v];
    obj.push(`v ${xOf(u).toFixed(4)} ${yOf(u).toFixed(4)} ${zTop(u).toFixed(4)}`);
    obj.push(`v ${xOf(v).toFixed(4)} ${yOf(v).toFixed(4)} ${zTop(v).toFixed(4)}`);
    obj.push(`v ${bv.x.toFixed(4)} ${bv.y.toFixed(4)} ${bv.z.toFixed(4)}`);
    obj.push(`v ${bu.x.toFixed(4)} ${bu.y.toFixed(4)} ${bu.z.toFixed(4)}`);
  }
  obj.push('');

  // Współrzędne UV – wspólne dla góry i dołu (1..n)
  obj.push('# UV coordinates');
  for (const { x, y } of vertices) {
    const u = (x + hw) / W;
    const v = (y + hh) / H;
    obj.push(`vt ${u.toFixed(6)} ${v.toFixed(6)}`);
  }
  obj.push('');

  // Normalne (1..n = góra, n+1..2n = dół, 2n+1.. = boki)
  obj.push('# Normals');
  for (let i = 0; i < n; i++) {
    const nrm = vertexNormals[i];
    obj.push(`vn ${nrm.x.toFixed(6)} ${nrm.y.toFixed(6)} ${nrm.z.toFixed(6)}`);
  }
  for (let i = 0; i < n; i++) {
    const nrm = vertexNormals[i];
    obj.push(`vn ${(-nrm.x).toFixed(6)} ${(-nrm.y).toFixed(6)} ${(-nrm.z).toFixed(6)}`);
  }
  for (const [u, v] of boundaryEdges) {
    // Normalna boczna – prostopadła do krawędzi u→v w płaszczyźnie XY
    const dx = xOf(v) - xOf(u);
    const dy = yOf(v) - yOf(u);
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    obj.push(`vn ${(dy / len).toFixed(6)} ${(-dx / len).toFixed(6)} 0`);
  }
  obj.push('');

  // ── Ściany – górna powierzchnia ──
  obj.push('g top');
  obj.push('usemtl mat_top');
  for (const [a, b, c] of triangles) {
    const va = a + 1; const vb = b + 1; const vc = c + 1;
    obj.push(`f ${va}/${va}/${va} ${vb}/${vb}/${vb} ${vc}/${vc}/${vc}`);
  }
  obj.push('');

  // ── Ściany – dolna powierzchnia (odwrócone zawijanie: a,c,b) ──
  obj.push('g bottom');
  obj.push('usemtl mat_bottom');
  for (const [a, b, c] of triangles) {
    const va = a + n + 1; const vb = b + n + 1; const vc = c + n + 1;
    const ua = a + 1;     const ub = b + 1;     const uc = c + 1;
    const na = a + n + 1; const nb = b + n + 1; const nc = c + n + 1;
    obj.push(`f ${va}/${ua}/${na} ${vc}/${uc}/${nc} ${vb}/${ub}/${nb}`);
  }
  obj.push('');

  // ── Ściany – boczne ──
  obj.push('g sides');
  obj.push('usemtl mat_sides');
  for (let i = 0; i < boundaryEdges.length; i++) {
    const vBase  = 2 * n + i * 4 + 1;
    const normIdx = 2 * n + i + 1;
    obj.push(`f ${vBase}//${normIdx} ${vBase + 1}//${normIdx} ${vBase + 2}//${normIdx}`);
    obj.push(`f ${vBase}//${normIdx} ${vBase + 2}//${normIdx} ${vBase + 3}//${normIdx}`);
  }

  // ── 4. MTL ───────────────────────────────────────────────

  const [sr, sg, sb] = hexToRgb(materialColor);
  const rgbStr = `${sr.toFixed(4)} ${sg.toFixed(4)} ${sb.toFixed(4)}`;

  const mtl: string[] = [];
  mtl.push('# Picture Dome Designer – Materials');
  mtl.push('');

  for (const [name, file] of [
    ['mat_top',    topTexFile],
    ['mat_bottom', bottomTexFile],
  ] as [string, string][]) {
    mtl.push(`newmtl ${name}`);
    mtl.push('Ka 1.0000 1.0000 1.0000');
    mtl.push('Kd 1.0000 1.0000 1.0000');
    mtl.push('Ks 0.0500 0.0500 0.0500');
    mtl.push('Ns 20.0');
    mtl.push('d 1.0');
    mtl.push('illum 2');
    mtl.push(`map_Kd ${file}`);
    mtl.push('');
  }

  mtl.push('newmtl mat_sides');
  mtl.push(`Ka ${rgbStr}`);
  mtl.push(`Kd ${rgbStr}`);
  mtl.push('Ks 0.0500 0.0500 0.0500');
  mtl.push('Ns 20.0');
  mtl.push('d 1.0');
  mtl.push('illum 2');

  // ── 5. ZIP + download ────────────────────────────────────

  const name = window.prompt(
    'Podaj nazwę pliku ZIP dla modelu 3D:',
    'sheet_3d',
  );
  if (name === null) return; // anulowano

  const filename = (name.trim() || 'sheet_3d').replace(/\.zip$/i, '') + '.zip';

  const zip = new JSZip();
  zip.file('sheet.obj', obj.join('\n'));
  zip.file('sheet.mtl', mtl.join('\n'));
  zip.file(topTexFile,  topTexDataUrl.split(',')[1],    { base64: true });
  if (bottomTexFile !== topTexFile) {
    zip.file(bottomTexFile, bottomTexDataUrl.split(',')[1], { base64: true });
  }

  const blob = await zip.generateAsync({
    type:               'blob',
    compression:        'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.download = filename;
  a.href     = url;
  a.click();
  URL.revokeObjectURL(url);
}
